import { TemplateElement, defineElement, html, classMap, when, optionalAttribute } from '@webtides/element-js';
import style from './button.css';

/**
 * @typedef {'highlight' | 'primary' | 'secondary' | 'tertiary' | 'quaternary'} ButtonVariant
 * @typedef {'sm' | 'md' | 'lg'} ButtonSize
 */

/**
 * Buttons represent actions that are available to the user.
 *
 * @property {string} type - The button type (button, submit, reset)
 * @property {string} [href] - URL for link buttons
 * @property {string} [target] - Target for link buttons
 * @property {boolean} disabled - Whether the button is disabled
 * @property {ButtonVariant} variant - Visual style variant: 'highlight', 'primary', 'secondary', 'tertiary', or 'quaternary'
 * @property {ButtonSize} size - Size variant: 'sm', 'md' (default), or 'lg'
 * @property {boolean} fullwidth - Whether the button should take full width
 * @property {string} element - Element identifier for tracking purposes
 * @property {string} dataTracking - Tracking payload (JSON) read by the global click tracker
 * @property {string} dataTrackingExtra - Additional tracking data to be passed to analytics
 *
 * @slot - The button's label.
 * @slot suffix - A presentational suffix icon or similar element.
 */
export default class Button extends TemplateElement {
    constructor() {
        super({
            styles: [style],
            shadowRender: true,
            propertyOptions: {
                variant: { reflect: true },
                fullwidth: { reflect: true },
            },
        });
    }

    properties() {
        return {
            type: 'button',
            href: '',
            target: '',
            disabled: false,
            variant: 'secondary',
            size: '', // '' (default/md) || 'sm' || 'lg'
            fullwidth: false,
            element: '', // TODO: this is for tracking... but the name is not perfect...
            dataTracking: '',
            dataTrackingExtra: '',
        };
    }

    events() {
        return {
            button: {
                click: (event) => {
                    if (this.type === 'submit') {
                        const form = this.closest('FORM');
                        form.requestSubmit();
                    }
                },
            },
        };
    }

    template() {
        const classes = {
            'is-highlight': this.variant === 'highlight',
            'is-primary': this.variant === 'primary',
            'is-secondary': this.variant === 'secondary',
            'is-tertiary': this.variant === 'tertiary',
            'is-quaternary': this.variant === 'quaternary',
            'is-sm': this.size === 'sm',
            'is-lg': this.size === 'lg',
            'is-fullwidth': this.fullwidth,
        };

        if (this.href) {
            return html`
                <a
                    href="${this.href}"
                    class="${classMap(classes)}"
                    ?disabled="${this.disabled}"
                    ${optionalAttribute(this.target, 'target', this.target)}
                    ${optionalAttribute(this.element, 'element', this.element)}
                    ${optionalAttribute(this.dataTracking, 'data-tracking', this.dataTracking)}
                    ${optionalAttribute(this.dataTrackingExtra, 'data-tracking-extra', this.dataTrackingExtra)}
                >
                    ${when(this.variant === 'highlight', html` <span class="gradient"></span> `)}
                    <slot></slot>
                    <slot name="suffix"></slot>
                </a>
            `;
        }

        return html`
            <button
                class="${classMap(classes)}"
                type="${this.type}"
                ?disabled="${this.disabled}"
                ${optionalAttribute(this.element, 'element', this.element)}
                ${optionalAttribute(this.dataTracking, 'data-tracking', this.dataTracking)}
                ${optionalAttribute(this.dataTrackingExtra, 'data-tracking-extra', this.dataTrackingExtra)}
            >
                ${when(this.variant === 'highlight', html` <span class="gradient"></span> `)}
                <slot></slot>
                <slot name="suffix"></slot>
            </button>
        `;
    }
}

defineElement('mb-button', Button);
